import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { OpenInNew, PhotoCamera, Save } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PortfolioUploader } from '../components/staff/PortfolioUploader';
import { UPDATE_STAFF_PROFILE } from '../graphql/mutations';
import { useConfirmation } from '../hooks/useConfirmation';
import { CustomSnackbar } from '../components/common/CustomSnackbar';

// Two modes:
// - Default (no `?staff=<id>`): edit the logged-in user's own profile via
//   the existing auth-scoped `myStaffDetails` query. Used by barbers.
// - Admin override (`?staff=<id>`): admins deep-link here from the Staff page
//   to edit a specific barber's public profile. The backend's
//   `check_staff_self_or_admin` decorator enforces admin-at-shared-store,
//   so admins can only reach barbers at their own shops.
const GET_MY_STAFF_PROFILE = gql`
  query GetMyStaffProfile {
    myStaffDetails {
      id
      firstName
      lastName
      fullName
      username
      slug
      bio
      title
      avatar
      instagramUrl
      facebookUrl
      whatsappUrl
      telegramUrl
      linkedinUrl
      tiktokUrl
      portfolio {
        id
        url
      }
    }
  }
`;

const GET_STAFF_PROFILE_BY_ID = gql`
  query GetStaffProfileById($staffId: ID!) {
    staffDetails(staffId: $staffId) {
      id
      firstName
      lastName
      fullName
      username
      slug
      bio
      title
      avatar
      instagramUrl
      facebookUrl
      whatsappUrl
      telegramUrl
      linkedinUrl
      tiktokUrl
      portfolio {
        id
        url
      }
    }
  }
`;

const CUSTOMER_APP_URL = import.meta.env.VITE_CUSTOMER_APP_URL || 'https://hairx.co.uk';
const BIO_LIMIT = 500;
const TITLE_LIMIT = 100;
const AVATAR_MAX_SIZE = 5 * 1024 * 1024; // 5 MB

// List-driven so adding/removing a social channel later is a one-line change.
// Each key matches the field name on Staff (camelCase) and the mutation arg.
const SOCIAL_FIELDS = [
  { key: 'instagramUrl', label: 'Instagram' },
  { key: 'facebookUrl', label: 'Facebook' },
  { key: 'tiktokUrl', label: 'TikTok' },
  { key: 'whatsappUrl', label: 'WhatsApp link' },
  { key: 'telegramUrl', label: 'Telegram link' },
  { key: 'linkedinUrl', label: 'LinkedIn' },
] as const;
type SocialKey = (typeof SOCIAL_FIELDS)[number]['key'];
type SocialsState = Record<SocialKey, string>;
const EMPTY_SOCIALS: SocialsState = {
  instagramUrl: '',
  facebookUrl: '',
  tiktokUrl: '',
  whatsappUrl: '',
  telegramUrl: '',
  linkedinUrl: '',
};

// Keep file → base64 local to this page; trivial enough not to warrant a util.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const MyProfile = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const adminTargetStaffId = searchParams.get('staff');
  const isAdminOverride = Boolean(adminTargetStaffId);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const {
    snackbarState,
    closeSnackbar,
    showSuccessSnackbar,
    showErrorSnackbar,
  } = useConfirmation();

  const selfQuery = useQuery(GET_MY_STAFF_PROFILE, {
    skip: isAdminOverride,
    fetchPolicy: 'cache-and-network',
  });
  const adminQuery = useQuery(GET_STAFF_PROFILE_BY_ID, {
    skip: !isAdminOverride,
    variables: { staffId: adminTargetStaffId },
    fetchPolicy: 'cache-and-network',
  });

  const loading = isAdminOverride ? adminQuery.loading : selfQuery.loading;
  const refetch = isAdminOverride ? adminQuery.refetch : selfQuery.refetch;
  const staff = isAdminOverride
    ? adminQuery.data?.staffDetails
    : selfQuery.data?.myStaffDetails;

  const [bio, setBio] = useState('');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [socials, setSocials] = useState<SocialsState>(EMPTY_SOCIALS);
  const [slugError, setSlugError] = useState<string | null>(null);
  // Avatar draft state — holds the in-memory preview + base64 payload until save.
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUpload, setAvatarUpload] = useState<{ data: string; name: string } | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Sync local state with the server record whenever the query returns.
  useEffect(() => {
    if (!staff) return;
    setBio(staff.bio || '');
    setTitle(staff.title || '');
    setSlug(staff.slug || '');
    setSocials({
      instagramUrl: staff.instagramUrl || '',
      facebookUrl: staff.facebookUrl || '',
      tiktokUrl: staff.tiktokUrl || '',
      whatsappUrl: staff.whatsappUrl || '',
      telegramUrl: staff.telegramUrl || '',
      linkedinUrl: staff.linkedinUrl || '',
    });
    // Drop any pending avatar upload; server state is now source of truth.
    setAvatarUpload(null);
    setAvatarPreview(null);
  }, [staff]);

  const [updateProfile, { loading: saving }] = useMutation(UPDATE_STAFF_PROFILE);

  const hasChange = useMemo(() => {
    if (!staff) return false;
    const socialsChanged = SOCIAL_FIELDS.some(
      ({ key }) => socials[key] !== (staff[key] || '')
    );
    return (
      avatarUpload !== null ||
      bio !== (staff.bio || '') ||
      title !== (staff.title || '') ||
      slug !== (staff.slug || '') ||
      socialsChanged
    );
  }, [bio, title, slug, socials, avatarUpload, staff]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);

    if (!file.type.startsWith('image/')) {
      setAvatarError('Avatar must be an image.');
      return;
    }
    if (file.size > AVATAR_MAX_SIZE) {
      setAvatarError('Avatar must be smaller than 5MB.');
      return;
    }

    try {
      const data = await fileToBase64(file);
      // Local object URL for instant preview; replaced by server URL on refetch.
      setAvatarPreview(URL.createObjectURL(file));
      setAvatarUpload({ data, name: file.name });
    } catch {
      setAvatarError('Could not read the file. Please try another.');
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!staff) return;
    setSlugError(null);
    try {
      const res = await updateProfile({
        variables: {
          staffId: staff.id,
          bio,
          title,
          slug,
          fileData: avatarUpload?.data,
          fileName: avatarUpload?.name,
          ...socials,
        },
      });
      const result = res.data?.updateStaffProfile;
      if (result?.ok) {
        showSuccessSnackbar('Profile updated.');
        await refetch();
      } else {
        const errs: string[] = result?.errors ?? [];
        if (errs.includes('slug_taken')) {
          setSlugError('This URL is already in use. Try a different one.');
        } else {
          showErrorSnackbar(errs.join(', ') || 'Could not update profile.');
        }
      }
    } catch {
      showErrorSnackbar('Could not update profile. Please try again.');
    }
  };

  if (loading && !staff) return <LoadingSpinner message="Loading your profile…" />;
  if (!staff) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No staff profile associated with this account.</Typography>
      </Box>
    );
  }

  const publicUrl = slug ? `${CUSTOMER_APP_URL}/staff/${slug}` : null;
  const displayAvatar = avatarPreview || staff.avatar || undefined;
  const initials = ((staff.firstName?.[0] || '') + (staff.lastName?.[0] || '')).toUpperCase();

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '820px',
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
          {isAdminOverride ? `${staff.fullName}'s profile` : 'My profile'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {isAdminOverride
            ? 'Edit this barber\'s public profile as an admin. Changes go live immediately on their public page.'
            : 'What shows up on your public barber page. Clients see this when they open your profile link.'}
        </Typography>

        {/* Avatar card — sits at the top so the self-editor can immediately see
            their photo. Preview shows the pending file before save; reverts on
            successful refetch. */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="overline" color="text.secondary">
              Profile photo
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 1.5 }}>
              <Avatar
                src={displayAvatar}
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: '2rem',
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                }}
              >
                {!displayAvatar && (initials || '?')}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleAvatarChange}
                />
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {displayAvatar ? 'Change photo' : 'Upload photo'}
                </Button>
                {avatarUpload && (
                  <Typography variant="caption" display="block" color="success.main" sx={{ mt: 1 }}>
                    New photo selected — save changes to apply.
                  </Typography>
                )}
                {avatarError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {avatarError}
                  </Alert>
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  JPG or PNG, max 5MB. Shown as a circle on your profile.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* About section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="overline" color="text.secondary">
              About
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={2.5} sx={{ mt: 1.5 }}>
              <TextField
                label="Title"
                helperText={`${title.length}/${TITLE_LIMIT} — e.g. "Fade specialist"`}
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_LIMIT))}
                fullWidth
                inputProps={{ maxLength: TITLE_LIMIT }}
              />
              <TextField
                label="Bio"
                helperText={`${bio.length}/${BIO_LIMIT}`}
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, BIO_LIMIT))}
                multiline
                rows={4}
                fullWidth
                inputProps={{ maxLength: BIO_LIMIT }}
              />
              <TextField
                label="Profile URL"
                value={slug}
                onChange={(e) => { setSlug(e.target.value.toLowerCase().trim()); setSlugError(null); }}
                error={!!slugError}
                helperText={
                  slugError ||
                  (publicUrl ? `Your page: ${publicUrl}` : 'Letters, numbers and dashes only')
                }
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">/staff/</InputAdornment>,
                  endAdornment: publicUrl && (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        endIcon={<OpenInNew fontSize="small" />}
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Social links — all optional, blank-to-clear. */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="overline" color="text.secondary">
              Social links
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2 }}>
              Optional. Any link you add appears as an icon on your public profile.
            </Typography>

            <Stack spacing={2}>
              {SOCIAL_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  placeholder="https://…"
                  value={socials[key]}
                  onChange={(e) => setSocials((prev) => ({ ...prev, [key]: e.target.value }))}
                  fullWidth
                  type="url"
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Portfolio */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="overline" color="text.secondary">
              Portfolio
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2 }}>
              Show off your work. These are displayed in a gallery on your public profile.
            </Typography>
            <PortfolioUploader
              staffId={staff.id}
              portfolio={staff.portfolio || []}
              onChange={refetch}
            />
          </CardContent>
        </Card>

        {!isAdminOverride && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Shop assignments and services are managed by your shop admin from the Staff page.
          </Alert>
        )}

        {/* Save action */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChange || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ borderRadius: 2, px: 3, py: 1.25, fontWeight: 600, minWidth: 160 }}
          >
            {saving ? 'Saving…' : hasChange ? 'Save changes' : 'No changes'}
          </Button>
        </Stack>
      </motion.div>

      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default MyProfile;
